import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, Plus, AlignLeft, AlignCenter, AlignRight, GripVertical, Check, Columns2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { useClubLogos, findLogo } from "@/lib/clubLogos";
import { POSTER_BG, newRow, newFooterRow, sortPoster } from "@/lib/posterTemplate";
import { PosterOverlays } from "@/components/PosterOverlays";

export const SIMPLE_DEFAULT = {
  font: "Bebas Neue", size: 30, title_color: "#ffffff", head_color: "#e8b94a", text_color: "#ffffff", cat_color: "#ffffff", time_color: "#ffffff", club_color: "#e8b94a",
  bold: false, italic: false, upper: false, ls: 0, align: "center", bg_opacity: 100, week_pos: "top", days_layout: "row", line_gap: 0, show_logos: true,
};
export const simpleOf = (data) => {
  const b = data.band || {};
  return { ...SIMPLE_DEFAULT, ...Object.fromEntries(Object.keys(SIMPLE_DEFAULT).filter((k) => b[k] !== undefined && b[k] !== "").map((k) => [k, b[k]])) };
};

const SHADOW = "0 2px 6px rgba(0,0,0,0.85)";
const JUSTIFY = { left: "flex-start", center: "center", right: "flex-end" };
const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const Club = ({ text, teams }) => {
  const list = (teams || []).filter((t) => t.name?.trim()).sort((a, b) => b.name.length - a.name.length);
  if (!list.length || !text) return text || "";
  const re = new RegExp(`(?<![\\p{L}\\d])(${list.map((t) => esc(t.name.trim())).join("|")})(?![\\p{L}\\d])`, "iu");
  return text.split(re).map((p, i) => {
    const t = i % 2 ? list.find((x) => x.name.trim().toLowerCase() === p.toLowerCase()) : null;
    return t ? <span key={i} style={{ color: t.color }}>{p}</span> : p;
  });
};

const DAYS_RE = /^(LUNDI|MARDI|MERCREDI|JEUDI|VENDREDI|SAMEDI|DIMANCHE)$/i;
const TIME_RE = /^(\d{1,2}[H:]\d{0,2}|F\.?G\.?)$/i;
export const parsePasted = (text, footer) => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
  const words = l.split(/\s+/);
  let cat = words.shift() || "";
  if (/^U\d+$/i.test(cat) && /^[A-H]$/i.test(words[0] || "")) cat += ` ${words.shift()}`;
  const ti = words.findIndex((w) => TIME_RE.test(w));
  const time = ti >= 0 ? words.splice(ti, 1)[0].toUpperCase() : "";
  const di = footer ? words.findIndex((w) => DAYS_RE.test(w)) : -1;
  const day = di >= 0 ? words.splice(di, 1)[0].toUpperCase() : "";
  const base = footer ? newFooterRow() : newRow();
  return { ...base, cat: cat.toUpperCase(), time, match: words.join(" ").replace(/^[-–]\s*/, ""), ...(footer && { day: day || base.day }) };
});

const baseName = (s) => (s || "").replace(/(\s+([A-H]|\d{1,2}))+\s*$/i, "").trim();
const Logo = ({ url }) => url ? (
  <img src={mediaUrl(url)} alt="" crossOrigin="anonymous" style={{ width: "1em", height: "1em", objectFit: "contain", display: "inline-block", verticalAlign: "-0.12em", marginRight: "0.25em", filter: "drop-shadow(0 1px 2px rgba(0,0,0,.7))" }} />
) : null;
const MatchSides = ({ text, teams, logos }) => {
  const parts = text.split(/(\s+-\s+)/);
  return parts.map((p, i) => (i % 2 ? <span key={i}>{p}</span> : (
    <span key={i}><Logo url={findLogo(logos.list, baseName(p))?.url} /><Club text={p} teams={teams} /></span>
  )));
};

const MARK = /\{(#[0-9a-f]{3,8})\|([^{}]*)\}/gi;
export const stripMarks = (t) => (t || "").replace(MARK, "$2");
const colorize = (v, a, b, c) => {
  const plain = stripMarks(v);
  if (plain !== v && !c) return plain;
  const part = v.slice(a, b);
  if (!c || /[{}|]/.test(part)) return c ? v : plain;
  return `${v.slice(0, a)}{${c}|${part}}${v.slice(b)}`;
};
const Rich = ({ text, render }) => {
  const out = [];
  let last = 0;
  text.replace(MARK, (m, c, t, i) => {
    if (i > last) out.push(<span key={last}>{render(text.slice(last, i))}</span>);
    out.push(<span key={i} style={{ color: c }}>{t}</span>);
    last = i + m.length;
    return m;
  });
  if (last < text.length) out.push(<span key={last}>{render(text.slice(last))}</span>);
  return out;
};
const PICKS = ["#e53935", "#e8b94a", "#ffffff", "#43a047", "#29b6f6", "#ff8a00"];
const ColorPicker = ({ onPick }) => (
  <span className="absolute bottom-full left-0 z-50 mb-1 flex items-center gap-1 rounded-md bg-slate-900/95 p-1 shadow-lg" style={{ textShadow: "none", fontSize: 14 }}
    onMouseDown={(e) => e.preventDefault()} data-testid="text-color-picker">
    {PICKS.map((c) => (
      <button key={c} onClick={() => onPick(c)} className="h-5 w-5 rounded-full border border-white/40" style={{ background: c }} title="Colorer la sélection" data-testid={`pick-${c.slice(1)}`} />
    ))}
    <button onClick={() => onPick(null)} className="px-1 font-sans text-[11px] text-white/80 hover:text-white" data-testid="pick-none">Sans couleur</button>
  </span>
);

const Field = ({ value, onChange, editable, placeholder, w, min, grow, align = "left", club, testid, onPasteLines, color, logos, dbl }) => {
  const [edit, setEdit] = useState(false);
  const [sel, setSel] = useState(null);
  const orig = useRef(value);
  const grab = (e) => setSel([e.currentTarget.selectionStart, e.currentTarget.selectionEnd]);
  const fit = grow !== undefined;
  const style = fit
    ? { flex: grow ? "1 1 0" : "0 1 auto", minWidth: 0, textAlign: align, whiteSpace: "nowrap", overflow: "hidden" }
    : { width: w, minWidth: min, flex: "none", textAlign: align, whiteSpace: "nowrap", color };
  if (editable && edit) return (
    <span className="relative" style={{ ...style, overflow: "visible" }}>
    {sel && sel[1] > sel[0] && <ColorPicker onPick={(c) => { onChange(colorize(value || "", sel[0], sel[1], c)); setSel(null); }} />}
    <input autoFocus value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} onBlur={() => { setEdit(false); setSel(null); }}
      onSelect={grab} onMouseUp={grab} onKeyUp={grab}
      onFocus={() => { orig.current = value; }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { onChange(orig.current); e.currentTarget.blur(); } }} data-testid={testid}
      onPaste={onPasteLines ? (e) => {
        const txt = e.clipboardData.getData("text");
        if (!/\n/.test(txt.trim())) return;
        e.preventDefault();
        setEdit(false);
        onPasteLines(txt);
      } : undefined}
      className="w-full rounded-sm bg-white/20 px-0 outline-none" style={{ font: "inherit", color: "inherit", letterSpacing: "inherit", textTransform: "inherit", textAlign: align }} />
    </span>
  );
  return (
    <span data-fit={fit ? "1" : undefined} style={style} data-testid={testid} title={editable && dbl ? "Double-clic pour modifier" : undefined}
      {...(editable && { [dbl ? "onDoubleClick" : "onClick"]: () => setEdit(true) })}
      className={`${editable ? (dbl ? "cursor-move rounded-sm hover:bg-white/10" : "cursor-text rounded-sm hover:bg-white/10") : ""} ${value || min ? "" : "relative inline-block h-[1em]"}`}>
      {value ? <Rich text={value} render={(t) => (logos ? <MatchSides text={t} teams={club} logos={logos} /> : <Club text={t} teams={club} />)} />
        : editable && <span className={min ? "" : "absolute bottom-0 left-0"} style={{ opacity: 0.4, whiteSpace: "nowrap" }}>{placeholder || "…"}</span>}
    </span>
  );
};

export const textExtent = (el) => {
  const rects = [];
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while (walk.nextNode()) if (walk.currentNode.textContent.trim()) {
    const r = document.createRange(); r.selectNodeContents(walk.currentNode); rects.push(...r.getClientRects());
  }
  el.querySelectorAll("img").forEach((i) => rects.push(i.getBoundingClientRect()));
  const box = el.getBoundingClientRect();
  if (!rects.length) return { l: box.left, r: box.right, t: box.top, b: box.bottom };
  return { l: Math.min(...rects.map((r) => r.left)), r: Math.max(...rects.map((r) => r.right)), t: Math.min(...rects.map((r) => r.top)), b: Math.max(...rects.map((r) => r.bottom)) };
};
const SNAP = 6;
const edgesX = (x) => [x.l, (x.l + x.r) / 2, x.r];
const edgesY = (x) => [x.t, (x.t + x.b) / 2, x.b];
const snap = (edges, targets, d) => {
  let best = null;
  for (const e of edges) for (const t of targets) { const diff = t - (e + d); if (Math.abs(diff) < SNAP && (!best || Math.abs(diff) < Math.abs(best.diff))) best = { diff, at: t }; }
  return best;
};
const guide = (root, vertical) => {
  const g = document.createElement("div");
  g.dataset.testid = vertical ? "align-guide-v" : "align-guide-h";
  Object.assign(g.style, { position: "absolute", zIndex: 60, pointerEvents: "none", background: "#22d3ee", boxShadow: "0 0 4px #22d3ee", display: "none",
    ...(vertical ? { top: 0, bottom: 0, width: "1px" } : { left: 0, right: 0, height: "1px" }) });
  root.appendChild(g);
  return g;
};

export const startDrag = (e, start, put, selfEl, { exclude = [], onTap } = {}) => {
  e.preventDefault();
  e.stopPropagation();
  const root = e.currentTarget.closest("[data-testid='usc-layout']");
  const box = root.getBoundingClientRect();
  const k = box.width / 1280;
  const self = selfEl || e.currentTarget.parentElement;
  const me = textExtent(self);
  const others = [...root.querySelectorAll("[data-line], [data-head], [data-snap]")].filter((x) => x !== self && !exclude.includes(x) && !x.closest("[aria-hidden]") && !self.contains(x)).map(textExtent);
  const tx = [box.left + box.width / 2, ...others.flatMap(edgesX)];
  const ty = others.flatMap(edgesY);
  const gv = guide(root, true), gh = guide(root, false);
  const sx = e.clientX, sy = e.clientY;
  let moved = false;
  const move = (ev) => {
    let ddx = ev.clientX - sx, ddy = ev.clientY - sy;
    if (!moved && Math.hypot(ddx, ddy) < 3) return;
    moved = true;
    const bx = ev.altKey ? null : snap(edgesX(me), tx, ddx);
    const by = ev.altKey ? null : snap(edgesY(me), ty, ddy);
    if (bx) { ddx += bx.diff; gv.style.left = `${(bx.at - box.left) / k}px`; }
    if (by) { ddy += by.diff; gh.style.top = `${(by.at - box.top) / k}px`; }
    gv.style.display = bx ? "block" : "none";
    gh.style.display = by ? "block" : "none";
    put({ dx: Math.round((start.dx || 0) + ddx / k), dy: Math.round((start.dy || 0) + ddy / k) });
  };
  const up = () => { gv.remove(); gh.remove(); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); if (!moved) onTap?.(); };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", up);
};
const shift = (o) => (o.dx || o.dy ? `translate(${o.dx || 0}px, ${o.dy || 0}px)` : undefined);
const Grip = ({ onDown, group, className = "-left-8 top-0", testid, title = "Déplacer (glisser)" }) => (
  <button onPointerDown={onDown} title={title} data-testid={testid} style={{ textShadow: "none", fontSize: 14 }}
    className={`pointer-events-none absolute ${className} z-30 flex h-6 w-8 cursor-grab touch-none items-center opacity-0 transition-opacity active:cursor-grabbing ${group}`}>
    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-orange-500 text-white shadow"><GripVertical className="h-4 w-4" /></span>
  </button>
);

const LineTools = ({ r, onChange, onRemove, selected, onSelect }) => (
  <div className="pointer-events-none absolute -top-6 right-0 z-30 flex gap-0.5 opacity-0 transition-opacity group-hover/l:pointer-events-auto group-hover/l:opacity-100" style={{ textShadow: "none" }}>
    {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Icon]) => (
      <button key={a} onClick={() => onChange({ ...r, al: r.al === a ? "" : a })} title="Alignement de la ligne" data-testid={`simple-align-${a}-${r.id}`}
        className={`flex h-6 w-6 items-center justify-center rounded ${r.al === a ? "bg-orange-500 text-white" : "bg-white/90 text-slate-900"}`}><Icon className="h-3.5 w-3.5" /></button>
    ))}
    <button onClick={onSelect} title="Sélectionner la ligne (taille, police, tri)" data-testid={`simple-select-${r.id}`}
      className={`flex h-6 w-6 items-center justify-center rounded ${selected ? "bg-sky-500 text-white" : "bg-white/90 text-slate-900"}`}><Check className="h-3.5 w-3.5" /></button>
    <button onClick={onRemove} title="Supprimer la ligne" data-testid={`simple-remove-${r.id}`}
      className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white"><X className="h-3.5 w-3.5" /></button>
  </div>
);

const Line = ({ r, s, editable, onChange, onRemove, onPasteLines, scores, footer, club, logos, selected, onSelect, onPress }) => {
  const al = r.al || s.align;
  const compact = al !== "left";
  const set = (k) => (v) => onChange({ ...r, [k]: v });
  const sep = <span style={{ width: "0.6em", flex: "none" }} />;
  return (
    <div data-line="1" className={`group/l relative flex items-baseline ${selected && editable ? "rounded-sm outline outline-2 outline-sky-400" : ""}`} data-testid={`simple-line-${r.id}`}
      onPointerDown={editable ? (e) => onPress(e, r) : undefined}
      style={{ touchAction: editable ? "none" : undefined, justifyContent: JUSTIFY[al], color: s.text_color, marginBottom: `${s.line_gap}em`, transform: shift(r), fontSize: r.s?.fs ? `${r.s.fs / 100}em` : undefined, fontFamily: r.s?.font ? `'${r.s.font}', sans-serif` : undefined }}>
      <Field value={r.cat} color={s.cat_color} dbl onChange={set("cat")} onPasteLines={onPasteLines} editable={editable} min={compact ? undefined : "3.2em"} placeholder="CAT" />{sep}
      {footer && <><Field value={r.day} onChange={set("day")} dbl color={s.time_color} editable={editable} min={compact ? undefined : "4.6em"} placeholder="JOUR" />&nbsp;</>}
      <Field value={scores ? r.score : r.time} dbl onChange={set(scores ? "score" : "time")} color={s.time_color} editable={editable} min={compact ? undefined : "3.6em"} placeholder={scores ? "SCORE" : "HEURE"} />{(!compact || (scores ? r.score : r.time)) && sep}
      <Field value={r.match} onChange={set("match")} dbl onPasteLines={onPasteLines} editable={editable} club={club} logos={logos} grow={!compact} placeholder="MATCH" />
      {editable && <LineTools r={r} onChange={onChange} onRemove={onRemove} selected={selected} onSelect={onSelect} />}
      {editable && <Grip group="group-hover/l:opacity-100 group-hover/l:pointer-events-auto" testid={`simple-move-${r.id}`} title="Déplacer la ligne (glisser)" onDown={(e) => onPress(e, r)} />}
    </div>
  );
};

const Add = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} data-add="1" title="Ajouter une ligne" style={{ textShadow: "none" }}
    className="absolute -left-16 bottom-0 z-40 flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-white/60 bg-black/40 text-white/80 hover:text-white">
    <Plus className="h-4 w-4" />
  </button>
);

const Block = ({ header, rows, s, editable, onHeader, onRows, scores, footer, testid, make = newRow, club, logos, pos = {}, onPos, sel = [], onSel, onPress }) => {
  const split = pos.split ?? (header !== undefined && rows.length > 4);
  return (
  <div className="relative min-w-0 flex-1" data-testid={testid}>
    {header !== undefined && <div data-head="1" className="group/h relative flex" style={{ fontSize: "1.25em", color: s.head_color, marginBottom: "0.2em", justifyContent: JUSTIFY[s.align], transform: shift(pos) }} data-testid={`${testid}-header`}>
      <Field value={header} onChange={onHeader} editable={editable} grow={s.align === "left"} placeholder="JOUR" />
      {editable && <>
        <Grip group="group-hover/h:opacity-100 group-hover/h:pointer-events-auto" testid={`${testid}-header-move`} title="Déplacer le titre seul (glisser)" onDown={(e) => startDrag(e, pos, (o) => onPos(o))} />
        <button onClick={() => onPos({ split: !split })} title="Matchs sur 2 colonnes (automatique au-delà de 4 matchs)" data-testid={`${testid}-split`} style={{ textShadow: "none", fontSize: 12 }}
          className={`absolute -top-6 right-0 z-30 flex h-6 items-center gap-1 rounded px-1.5 font-sans opacity-0 transition-opacity group-hover/h:opacity-100 ${split ? "bg-orange-500 text-white" : "bg-white/90 text-slate-900"}`}>
          <Columns2 className="h-3.5 w-3.5" /> 2 colonnes
        </button>
      </>}
    </div>}
    <div style={split ? { display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "1.5em", gridAutoFlow: "column", gridTemplateRows: `repeat(${Math.ceil(rows.length / 2) || 1}, auto)` } : undefined}>
    {rows.map((r, i) => (
      <Line key={r.id} r={r} s={s} editable={editable} scores={scores} footer={footer} club={club} logos={logos}
        selected={sel.includes(r.id)} onSelect={() => onSel(r.id)} onPress={onPress}
        onPasteLines={(txt) => { const add = parsePasted(txt, footer); const blank = !r.match || /ÉQUIPE A/.test(r.match); onRows([...rows.slice(0, blank ? i : i + 1), ...add, ...rows.slice(i + 1)], true); toast.success(`${add.length} match(s) collé(s)`); }}
        onChange={(nr) => onRows(rows.map((x) => (x.id === r.id ? nr : x)))} onRemove={() => onRows(rows.filter((x) => x.id !== r.id))} />
    ))}
    </div>
    {editable && <Add onClick={() => onRows([...rows, make()])} testid={`${testid}-add`} />}
  </div>
  );
};

const Movable = ({ id, data, editable, onChange, className = "", handle = "-left-9 top-0", children }) => {
  const m = ((data.band || {}).moves || {})[id] || { dx: 0, dy: 0 };
  const grab = (e) => startDrag(e, m, (v) => { const band = data.band || {}; onChange({ ...data, band: { ...band, moves: { ...(band.moves || {}), [id]: v } } }); });
  return (
    <div data-snap="1" className={`group/m relative ${className}`} style={m.dx || m.dy ? { transform: `translate(${m.dx}px, ${m.dy}px)` } : undefined} data-testid={`move-block-${id}`}>
      {children}
      {editable && (
        <button onPointerDown={grab} title="Déplacer le bloc (glisser)" data-testid={`move-handle-${id}`} style={{ textShadow: "none" }}
          className={`absolute ${handle} z-30 flex h-7 w-7 cursor-grab touch-none items-center justify-center rounded-md bg-orange-500 text-white opacity-0 shadow transition-opacity active:cursor-grabbing group-hover/m:opacity-100`}>
          <GripVertical className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

const Content = ({ data, editable, onChange, height, k, tk, boxRef, titleRef }) => {
  const s = simpleOf(data);
  const set = (patch) => onChange({ ...data, ...patch });
  const scores = false;
  const club = [{ name: (data.team_name || "USC").trim(), color: s.club_color }, ...((data.band || {}).team_colors || [])];
  const top = data.top || { header: "", rows: [] };
  const ORDER = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];
  const rank = (c) => { const i = ORDER.indexOf((c.header || "").trim().slice(0, 3).toUpperCase()); return i < 0 ? 9 : i; };
  const cols = (data.columns || []).slice(0, 2).map((c, i) => ({ c, i })).sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i);
  const setCol = (i, patch) => set({ columns: (data.columns || []).map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const bg = data.background_url ? mediaUrl(data.background_url) : POSTER_BG;
  const text = { fontFamily: `'${s.font}', sans-serif`, fontWeight: s.bold ? 700 : 400, fontStyle: s.italic ? "italic" : "normal",
    textTransform: s.upper ? "uppercase" : "none", letterSpacing: `${s.ls / s.size}em`, textShadow: SHADOW, lineHeight: 1.15 };
  const list = useClubLogos();
  const logos = s.show_logos && list.length ? { list } : null;
  const sel = data._sel || [];
  const onSel = (id) => set({ _sel: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
  const mapRows = (d, fn) => ({ ...d, top: d.top ? { ...d.top, rows: (d.top.rows || []).map(fn) } : d.top,
    columns: (d.columns || []).map((c) => ({ ...c, rows: (c.rows || []).map(fn) })), footer: (d.footer || []).map(fn) });
  const rowsAll = [...(top.rows || []), ...(data.columns || []).flatMap((c) => c.rows || []), ...(data.footer || [])];
  const onPress = (e, r) => {
    if (e.button !== 0 || e.target.closest("input, [data-testid='text-color-picker'], button:not([data-testid^='simple-move-'])")) return;
    const multi = e.shiftKey || e.ctrlKey || e.metaKey;
    const next = multi ? (sel.includes(r.id) ? sel.filter((x) => x !== r.id) : [...sel, r.id]) : (sel.includes(r.id) ? sel : [r.id]);
    const base = { ...data, _sel: next };
    if (next !== sel) onChange(base);
    if (!next.includes(r.id)) return e.preventDefault();
    const line = e.currentTarget.closest("[data-line]");
    const root = line.closest("[data-testid='usc-layout']");
    const starts = Object.fromEntries(rowsAll.filter((x) => next.includes(x.id)).map((x) => [x.id, { dx: x.dx || 0, dy: x.dy || 0 }]));
    const els = next.map((id) => [...root.querySelectorAll(`[data-testid="simple-line-${id}"]`)].find((x) => !x.closest("[aria-hidden]"))).filter(Boolean);
    startDrag(e, { dx: 0, dy: 0 }, ({ dx, dy }) => onChange(mapRows(base, (x) => (starts[x.id] ? { ...x, dx: starts[x.id].dx + dx, dy: starts[x.id].dy + dy } : x))), line,
      { exclude: els, onTap: () => { if (!multi && next.length > 1) onChange({ ...base, _sel: [r.id] }); } });
  };
  const p = { s, editable, scores, club, logos, sel, onSel, onPress };
  const mv = { data, editable, onChange };
  const posOf = (o) => ({ dx: o.dx, dy: o.dy, split: o.split });
  const week = (top.rows.length > 0 || (editable && top.show)) && (
    <Block {...p} header={top.header} rows={top.rows} testid="simple-top" pos={posOf(top)} onPos={(o) => set({ top: { ...top, ...o } })}
      onHeader={(v) => set({ top: { ...top, header: v } })} onRows={(rows, sort) => (sort ? onChange(sortPoster({ ...data, top: { ...top, rows, show: true } })) : set({ top: { ...top, rows, show: true } }))} />
  );
  return (
    <div className="relative w-[1280px] overflow-hidden" style={{ height, backgroundColor: "#0a1730", ...text }}
      onPointerDown={editable && sel.length ? (e) => { if (!e.target.closest("[data-line], button, input, [data-testid='text-color-picker']")) set({ _sel: [] }); } : undefined}>
      <div className="absolute inset-0" style={{ backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: s.bg_opacity / 100 }} />
      <div className="absolute inset-0 flex flex-col justify-center py-6">
      <Movable id="title" {...mv} className="shrink-0" handle="left-6 top-1">
      <div ref={titleRef} className="relative flex shrink-0 justify-center px-16" style={{ fontSize: s.size * 2 * tk, color: s.title_color, lineHeight: 1.1 }} data-testid="simple-title">
        <Field value={data.title} onChange={(v) => set({ title: v })} editable={editable} placeholder="TITRE" />
      </div>
      </Movable>
      <div ref={boxRef} className="relative mx-auto w-[1088px] shrink-0 space-y-4 pt-4" style={{ fontSize: s.size * k }}>
        {s.week_pos !== "bottom" && week}
        <div className={s.days_layout === "column" ? "space-y-4" : "flex gap-16"}>
          {cols.map(({ c, i }, pos) => (
            <Block key={c.id} {...p} header={c.header} rows={c.rows} testid={`simple-col-${pos}`} pos={posOf(c)} onPos={(o) => setCol(i, o)}
              onHeader={(v) => setCol(i, { header: v })} onRows={(rows, sort) => (sort ? onChange(sortPoster({ ...data, columns: (data.columns || []).map((c, j) => (j === i ? { ...c, rows } : c)) })) : setCol(i, { rows }))} />
          ))}
        </div>
        {s.week_pos === "bottom" && week}
        {((data.footer || []).length > 0 || editable) && (
          <Block {...p} rows={data.footer || []} footer testid="simple-footer" make={newFooterRow}
            onRows={(footer, sort) => (sort ? onChange(sortPoster({ ...data, footer, show_footer: true })) : set({ footer, show_footer: true }))} />
        )}
      </div>
      </div>
      <PosterOverlays data={data} editable={editable} onChange={onChange} />
    </div>
  );
};

const overflowRatio = (el) => Math.min(1, ...[...el.querySelectorAll("[data-fit]")].map((x) => (x.scrollWidth > x.clientWidth + 1 ? x.clientWidth / x.scrollWidth : 1)));

const fits = (box, size, k, height, titleH) => {
  box.style.fontSize = `${size * k}px`;
  const wide = [...box.querySelectorAll("[data-line]")].some((l) => l.scrollWidth > l.clientWidth + 1);
  return titleH + box.scrollHeight + 48 <= height && overflowRatio(box) >= 1 && !wide;
};
const search = (ok) => {
  if (ok(1)) return 1;
  let lo = 0.3, hi = 1;
  for (let i = 0; i < 12; i++) { const m = (lo + hi) / 2; if (ok(m)) lo = m; else hi = m; }
  return lo;
};

export const UscLayout = ({ data, editable, onChange, height = 720 }) => {
  const [k, setK] = useState(1);
  const [tk, setTk] = useState(1);
  const [fontTick, setFontTick] = useState(0);
  const box = useRef(null);
  const title = useRef(null);
  useEffect(() => {
    const bump = () => setFontTick((n) => n + 1);
    document.fonts?.ready.then(bump);
    document.fonts?.addEventListener?.("loadingdone", bump);
    return () => document.fonts?.removeEventListener?.("loadingdone", bump);
  }, []);
  useLayoutEffect(() => {
    const el = box.current;
    const t = title.current;
    if (!el || !t) return;
    const s = simpleOf(data);
    const span = t.querySelector("span");
    const nt = search((x) => { t.style.fontSize = `${s.size * 2 * x}px`; return !span || span.scrollWidth <= t.clientWidth - 128; });
    t.style.fontSize = `${s.size * 2 * nt}px`;
    const nk = search((x) => fits(el, s.size, x, height, t.offsetHeight));
    el.style.fontSize = `${s.size * nk}px`;
    setK(nk);
    setTk(nt);
  }, [data, height, fontTick]);
  const measure = <Content data={data} editable={false} onChange={() => {}} height={height} k={k} tk={tk} boxRef={box} titleRef={title} />;
  return (
    <div className="relative" style={{ width: 1280, height }} data-testid="usc-layout">
      {editable ? (
        <>
          <div aria-hidden className="pointer-events-none absolute left-0 top-0" style={{ visibility: "hidden" }}>{measure}</div>
          <Content data={data} editable onChange={onChange} height={height} k={k} tk={tk} />
        </>
      ) : measure}
    </div>
  );
};
